import { co2 } from "@tgwf/co2";

import type { components } from "./openapi/generated-types.ts";

// Sustainable Web Design Model v4 via co2.js (Green Web Foundation) als Quelle
// der Wahrheit. Die Netz-Intensität wird mit der Library gepflegt, statt eine
// eingefrorene Konstante zu hinterlegen. perVisit ist in v4 linear in den Bytes,
// daher genügt eine Konstante pro MB (grid bzw. green-hosted).
const swd = new co2({ model: "swd", version: 4 });
const ONE_MB = 1024 * 1024;

const co2GramsPerMb = (green: boolean): number => {
  const result = swd.perVisit(ONE_MB, green);
  return typeof result === "number" ? result : 0;
};

export const CO2_PER_MB_GRID = co2GramsPerMb(false);
export const CO2_PER_MB_GREEN = co2GramsPerMb(true);

export type PageSpeedMetrics = components["schemas"]["PageSpeedMetrics"];

export interface PageSpeedAnalysis {
  metrics: PageSpeedMetrics;
  co2PerVisitGrams: number;
  bytesSavedPerVisit: number;
  co2SavedPerVisitGrams: number;
  bytesSavedPerReturningVisit: number;
  co2SavedPerReturningVisitGrams: number;
  co2ActiveCompressionSavedGrams: number;
  activeCacheSavedBytes: number;
  co2ActiveCacheSavedGrams: number;
}

export const deriveAnalysis = (
  metrics: PageSpeedMetrics,
  options?: { greenHosted?: boolean },
): PageSpeedAnalysis => {
  const co2PerMb = options?.greenHosted ? CO2_PER_MB_GREEN : CO2_PER_MB_GRID;
  const mb = (b: number) => b / (1024 * 1024);
  const bytesSavedPerVisit = metrics.textCompressionSavingsBytes;
  // Doppelzählung vermeiden: Eine Ressource, die weder komprimiert noch gecached
  // ist, taucht in beiden PSI-Zahlen auf. Bei Wiederbesuch ist die Ressource
  // vollständig im Browser-Cache – die Compression-Einsparung greift *zusätzlich*
  // nur bei First-Visits. Daher Cache-waste um den Compression-Anteil bereinigen.
  const bytesSavedPerReturningVisit = Math.max(
    0,
    metrics.longCacheWastedBytes - metrics.textCompressionSavingsBytes,
  );
  // Beim wiederholten Seitenaufruf (Folgeseite oder Wiederbesuch) werden die
  // gecachten Assets nicht neu übertragen. totalBytes − longCacheWastedBytes
  // schätzt den Anteil, der aktuell tatsächlich cachefähig ist.
  const activeCacheSavedBytes = Math.max(
    0,
    metrics.totalBytes - metrics.longCacheWastedBytes,
  );
  return {
    metrics,
    co2PerVisitGrams: mb(metrics.totalBytes) * co2PerMb,
    bytesSavedPerVisit,
    co2SavedPerVisitGrams: mb(bytesSavedPerVisit) * co2PerMb,
    bytesSavedPerReturningVisit,
    co2SavedPerReturningVisitGrams: mb(bytesSavedPerReturningVisit) * co2PerMb,
    co2ActiveCompressionSavedGrams:
      mb(metrics.activeCompressionSavedBytes) * co2PerMb,
    activeCacheSavedBytes,
    co2ActiveCacheSavedGrams: mb(activeCacheSavedBytes) * co2PerMb,
  };
};

export const formatBytes = (bytes: number): string => {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const formatGrams = (grams: number): string => {
  return `${grams.toFixed(2)} g`;
};

const CO2_PER_CAR_KM_GRAMS = 120;

export const describeCarEquivalent = (grams: number): string | null => {
  if (grams <= 0) return null;
  const km = grams / CO2_PER_CAR_KM_GRAMS;
  if (km < 0.1) return null;
  return `${km.toFixed(1)} km Autofahrt`;
};

// Vergleicht zwei PageSpeedSettings auf semantische Gleichheit (Reihenfolge von
// Custom-Rule-Arrays wird normalisiert), um Stale-Zustand einer Messung zu
// erkennen.
type PageSpeedSettings = components["schemas"]["PageSpeedSettings"];

const normalize = (settings: PageSpeedSettings): string => {
  const normalized = {
    gzip: {
      enabled: settings.gzip.enabled,
      customMimes: [...settings.gzip.customMimes].sort(),
    },
    caching: {
      enabled: settings.caching.enabled,
      static: settings.caching.static,
      dynamic: settings.caching.dynamic,
      feeds: settings.caching.feeds,
      custom: [...settings.caching.custom].sort((a, b) =>
        a.mimeType.localeCompare(b.mimeType),
      ),
    },
  };
  return JSON.stringify(normalized);
};

export const isSettingsEqual = (
  a: PageSpeedSettings,
  b: PageSpeedSettings,
): boolean => normalize(a) === normalize(b);
